import { bitcoinjs } from '@optimex-xyz/btc-script'
import {
  BtcAddressFormat,
  getAddressFormatFromAddress,
  getConnection,
  getProvider,
} from '@optimex-xyz/trade-wallet-sdk'
import { PublicKey } from '@solana/web3.js'

import { isBtcChain, isEvmChain, isSolanaChain } from '@/utils/chain'
import { SUPPORTED_NETWORK } from '@/utils/constants'

import { ethers } from 'ethers'
import { z } from 'zod'

import { isEvmContractAddress, isSolanaContractAddress } from './contract'

const addressRegexes: Partial<Record<SUPPORTED_NETWORK, RegExp>> = {
  [SUPPORTED_NETWORK.BTC]: /^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$|^(bc1)[a-z0-9]{39,59}$/,
  [SUPPORTED_NETWORK.BTC_TESTNET]: /^[mn2][a-km-zA-HJ-NP-Z1-9]{25,34}$|^(tb1)[a-z0-9]{39,59}$/,
}

function isValidBitcoinAddress(address: string) {
  // Try mainnet first
  try {
    bitcoinjs.address.toOutputScript(address, bitcoinjs.networks.bitcoin)
    return true
  } catch {}

  // Try testnet if mainnet fails
  try {
    bitcoinjs.address.toOutputScript(address, bitcoinjs.networks.testnet)
    return true
  } catch {
    return false
  }
}

export function isValidSolanaAddress(address: string): boolean {
  try {
    const publicKey = new PublicKey(address)
    return PublicKey.isOnCurve(publicKey.toBytes()) // Ensure it's a valid public key
  } catch {
    return false
  }
}

const validateAddress = (networkId: string, recipientAddress: string, chainName?: string) => {
  const getErrorMessage = () => {
    if (isEvmChain(networkId)) return 'error.INVALID_EVM_ADDRESS'
    if (isSolanaChain(networkId)) return 'error.INVALID_SOL_ADDRESS'
    if (networkId === 'btc') return 'error.INVALID_BTC_ADDRESS'
    return `Invalid ${chainName || 'address'} format.`
  }
  const recipientAddressSchema = z.object({
    recipientAddress: z
      .string()
      .min(1, `Receiving address ${chainName} address is required.`)
      .refine((value) => {
        if (isEvmChain(networkId)) return ethers.isAddress(value)
        if (isSolanaChain(networkId)) return isValidSolanaAddress(value)

        const regex = addressRegexes[networkId as SUPPORTED_NETWORK]
        return regex?.test(value) && isValidBitcoinAddress(value)
      }, getErrorMessage()),
  })
  recipientAddressSchema.parse({ recipientAddress })
}

/**
 * @returns error message
 */
export const validateRecipientAddress = (value: string, chain: string, chainName?: string) => {
  try {
    validateAddress(chain, value, chainName)
  } catch (error) {
    if (error instanceof z.ZodError) {
      return error.errors[0].message
    }
    return 'Invalid receiving address'
  }
}

export const isAddress = (value: string, chain: string) => {
  try {
    validateAddress(chain, value)
    return true
  } catch {
    return false
  }
}

const ADDRESS_TYPES: Partial<Record<string, string>> = {
  [BtcAddressFormat.P2TR]: 'Taproot',
  [BtcAddressFormat.P2WPKH]: 'Native Segwit',
  [BtcAddressFormat.P2SH]: 'Nested Segwit',
  [BtcAddressFormat.P2PKH]: 'Legacy',
}

export const getBtcWalletAddressTypeLabel = (address: string) => {
  try {
    return ADDRESS_TYPES[getAddressFormatFromAddress(address || '')] ?? ''
  } catch {
    return ''
  }
}

export const isContractAddress = async (networkId: string, address: string) => {
  if (isBtcChain(networkId)) return false
  if (isEvmChain(networkId)) {
    const provider = getProvider(networkId)
    return await isEvmContractAddress(address, provider)
  }
  if (isSolanaChain(networkId)) {
    const connection = getConnection(networkId)
    return await isSolanaContractAddress(address, connection)
  }
  return false
}
export function isSameAddress(address1: string | undefined, address2: string | undefined): boolean {
  if (!address1 || !address2) return false
  return address1.toLowerCase() === address2.toLowerCase()
}
